import { GROUP_CLS } from "../../../utils/constants";
import { ApplyFunction, ApplyFunctionUtils } from "../ApplyFunction";
import { DetailedSafeObject, DetailedSafeObjectUtils } from "./IDetailedSafeObjecet";
import { DetailedPolicy, DetailedPolicyUtils } from "./DetailedPolicy";
import { DetailedUser, DetailedUserUtils } from "./DetailedUser";
import { Process, ProcessUtils } from "../Process";
import { Group } from "../cmds/Group";

export interface DetailedGroup extends DetailedSafeObject {
  readonly name: string;
  readonly nestedUsers: DetailedUser[];
  readonly nestedPolicies: DetailedPolicy[];
  readonly groupOwner: string[];
}

export const DetailedGroupUtils = {
  create({
    slot,
    timestamp,
    last_modified,
    isDeleted,
    isValid,
    applyFunction,
    originalObjectSIG,
    name,
    users,
    policies,
    id,
    groupOwner,
    parentGroup,
    SIGNATURE,
    comment,
    process,
    nestedObjects,
    source,
    destination,
    pending_objects,
    affected_by,
    execute,
  }: {
    slot: number;
    timestamp: number;
    last_modified: number;
    isDeleted: boolean;
    isValid: boolean;
    applyFunction: ApplyFunction | null;
    originalObjectSIG: string;
    name: string;
    users: DetailedUser[];
    policies: DetailedPolicy[];
    id: string;
    groupOwner: string[];
    parentGroup: string;
    SIGNATURE: string;
    comment: string;
    process: Process;
    nestedObjects: DetailedSafeObject[];
    source: string;
    destination: string;
    pending_objects: DetailedSafeObject[];
    affected_by: DetailedSafeObject[];
    execute: boolean;
  }): DetailedGroup {
    return {
      ...DetailedSafeObjectUtils.create({
        last_modified,
        affected_by,
        slot,
        timestamp,
        applyFunction,
        originalObjectSIG,
        isDeleted,
        cls: GROUP_CLS,
        id,
        parentGroup,
        SIGNATURE,
        comment,
        isValid,
        nestedObjects,
        process,
        source,
        destination,
        pending_objects,
        execute,
      }),
      name,
      nestedUsers: users,
      nestedPolicies: policies,
      groupOwner,
    };
  },

  fromGroup({
    group,
    groupOwner,
    policies,
    nestedObjects,
    users,
    applyFunction,
    SIGNATURE,
    isDeleted,
    isValid,
    originalObjectSIG,
    source,
    destination,
    slot,
    timestamp,
    last_modified,
    affected_by,
  }: {
    group: Group;
    groupOwner?: string[];
    policies?: DetailedPolicy[];
    nestedObjects?: DetailedSafeObject[];
    users?: DetailedUser[];
    applyFunction?: ApplyFunction | null;
    SIGNATURE?: string;
    isDeleted?: boolean;
    isValid?: boolean;
    originalObjectSIG?: string;
    source?: string;
    destination?: string;
    slot?: number;
    timestamp?: number;
    last_modified?: number;
    affected_by?: DetailedSafeObject[];
  }): DetailedGroup {
    const tms = Date.now();
    return DetailedGroupUtils.create({
      slot: slot ?? -1,
      last_modified: last_modified ?? tms,
      affected_by: affected_by ?? [],
      timestamp: timestamp ?? -1,
      source: source ?? "",
      destination: destination ?? "",
      SIGNATURE: SIGNATURE ?? group.SIGNATURE,
      applyFunction: applyFunction ?? group.applyFunction ?? ApplyFunctionUtils.empty(),
      comment: group.comment,
      groupOwner: groupOwner == null ? [group.parentGroup] : [...groupOwner, group.parentGroup],
      id: group.id,
      isDeleted: isDeleted ?? false,
      isValid: isValid ?? false,
      name: group.name,
      nestedObjects: nestedObjects ?? [],
      originalObjectSIG: originalObjectSIG ?? group.originalObjectSIG,
      parentGroup: group.parentGroup,
      policies: policies ?? [],
      users: users ?? [],
      process: ProcessUtils.empty(),
      pending_objects: [],
      execute: group.execute,
    });
  },

  empty(): DetailedGroup {
    return DetailedGroupUtils.create({
      ...DetailedSafeObjectUtils.empty(),
      name: "",
      policies: [],
      users: [],
      groupOwner: [],
    });
  },
  findGroupById(groups: DetailedGroup[], id: string): DetailedGroup {
    return groups.find((g) => g.id === id) || DetailedGroupUtils.empty();
  },

  toMap(group: DetailedGroup): Record<string, any> {
    const json = DetailedSafeObjectUtils.toMap(group);
    json["object"] = {
      ...json["object"],
      name: group.name,
    };
    json["users"] = group.nestedUsers.map((u) => DetailedUserUtils.toMap(u));
    json["policies"] = group.nestedPolicies.map((p) => DetailedPolicyUtils.toMap(p));
    json["groupOwner"] = group.groupOwner;
    json["nestedObjects"] = group.nestedObjects.map((o) => DetailedSafeObjectUtils.toMap(o));
    return json;
  },

  toJson(group: DetailedGroup): string {
    return JSON.stringify(DetailedGroupUtils.toMap(group));
  },

  copyWith({
    users,
    policies,
    id,
    groupOwner,
    parentGroup,
    name,
    SIGNATURE,
    comment,
    process,
    nestedObjects,
    isValid,
    isDeleted,
    applyFunction,
    originalObjectSIG,
    source,
    destination,
    slot,
    timestamp,
    last_modified,
    pending_objects,
    affected_by,
    execute,
  }: {
    users?: DetailedUser[];
    policies?: DetailedPolicy[];
    id?: string;
    groupOwner?: string[];
    parentGroup?: string;
    name?: string;
    SIGNATURE?: string;
    comment?: string;
    process?: Process;
    nestedObjects?: DetailedSafeObject[];
    isValid?: boolean;
    isDeleted?: boolean;
    applyFunction?: ApplyFunction | null;
    originalObjectSIG?: string;
    source?: string;
    destination?: string;
    slot?: number;
    timestamp?: number;
    last_modified?: number;
    pending_objects?: DetailedSafeObject[];
    affected_by?: DetailedSafeObject[];
    execute?: boolean;
  }): DetailedGroup {
    return DetailedGroupUtils.create({
      slot: slot ?? -1,
      last_modified: last_modified ?? Date.now(),
      timestamp: timestamp ?? -1,
      source: source ?? "",
      destination: destination ?? "",
      originalObjectSIG: originalObjectSIG ?? "",
      applyFunction: applyFunction ?? null,
      isDeleted: isDeleted ?? false,
      SIGNATURE: SIGNATURE ?? "",
      comment: comment ?? "",
      groupOwner: groupOwner ?? [],
      id: id ?? "",
      name: name ?? "",
      nestedObjects: nestedObjects ?? [],
      parentGroup: parentGroup ?? "",
      policies: policies ?? [],
      process: process ?? ProcessUtils.empty(),
      users: users ?? [],
      isValid: isValid ?? false,
      pending_objects: pending_objects ?? [],
      affected_by: affected_by ?? [],
      execute: execute ?? true,
    });
  },
};

// export default class DetailedGroup extends DetailedSafeObject {

//   readonly name: string;
//   readonly nestedUsers: DetailedUser[];
//   readonly nestedPolicies: DetailedPolicy[];
//   readonly groupOwner: string[];

//   constructor({
//     slot,
//     timestamp,
//     last_modified,
//     isDeleted,
//     isValid,
//     applyFunction,
//     originalObjectSIG,
//     name,
//     users,
//     policies,
//     id,
//     groupOwner,
//     parentGroup,
//     SIGNATURE,
//     comment,
//     process,
//     nestedObjects,
//     source,
//     destination,
//     pending_objects,
//     affected_by,
//     execute,
//   }: {
//     slot: number;
//     timestamp: number;
//     last_modified: number;
//     isDeleted: boolean;
//     isValid: boolean;
//     applyFunction: ApplyFunction | null;
//     originalObjectSIG: string;
//     name: string;
//     users: DetailedUser[];
//     policies: DetailedPolicy[];
//     id: string;
//     groupOwner: string[];
//     parentGroup: string;
//     SIGNATURE: string;
//     comment: string;
//     process: Process;
//     nestedObjects: DetailedSafeObject[];
//     source: string;
//     destination: string;
//     pending_objects: DetailedSafeObject[];
//     affected_by: DetailedSafeObject[];
//     execute: boolean;
//   }) {
//     super({
//       last_modified,
//       affected_by,
//       slot,
//       timestamp,
//       uiMetaData: UiMetaData.empty(),
//       applyFunction,
//       originalObjectSIG,
//       isDeleted,
//       cls: GROUP_CLS,
//       id,
//       parentGroup,
//       SIGNATURE,
//       comment,
//       isValid,
//       nestedObjects,
//       process,
//       source,
//       destination,
//       pending_objects,
//       execute,
//     });
//     this.name = name;
//     this.nestedUsers = users;
//     this.nestedPolicies = policies;
//     this.groupOwner = groupOwner;
//   }

//   static fromGroup({
//     group,
//     groupOwner,
//     policies,
//     nestedObjects,
//     users,
//     applyFunction,
//     SIGNATURE,
//     isDeleted,
//     isValid,
//     originalObjectSIG,
//     source,
//     destination,
//     slot,
//     timestamp,
//     last_modified,
//     affected_by,
//   }: {
//     group: Group;
//     groupOwner?: string[];
//     policies?: DetailedPolicy[];
//     nestedObjects?: DetailedSafeObject[];
//     users?: DetailedUser[];
//     applyFunction?: ApplyFunction | null;
//     SIGNATURE?: string;
//     isDeleted?: boolean;
//     isValid?: boolean;
//     originalObjectSIG?: string;
//     source?: string;
//     destination?: string;
//     slot?: number;
//     timestamp?: number;
//     last_modified?: number;
//     affected_by?: DetailedSafeObject[];
//   }): DetailedGroup {
//     const tms = Date.now();
//     return new DetailedGroup({
//       slot: slot ?? -1,
//       last_modified: last_modified ?? tms,
//       affected_by: affected_by ?? [],
//       timestamp: timestamp ?? -1,
//       source: source ?? "",
//       destination: destination ?? "",
//       SIGNATURE: SIGNATURE ?? group.SIGNATURE,
//       applyFunction: applyFunction ?? group.applyFunction ?? ApplyFunction.empty(),
//       comment: group.comment,
//       groupOwner: groupOwner == null ? [group.parentGroup] : [...groupOwner, group.parentGroup],
//       id: group.id,
//       isDeleted: isDeleted ?? false,
//       isValid: isValid ?? false,
//       name: group.name,
//       nestedObjects: nestedObjects ?? [],
//       originalObjectSIG: originalObjectSIG ?? group.originalObjectSIG,
//       parentGroup: group.parentGroup,
//       policies: policies ?? [],
//       users: users ?? [],
//       process: Process.empty(),
//       pending_objects: [],
//       execute: group.execute,
//     });
//   }

//   static empty(): DetailedGroup {
//     const tms = Date.now();
//     return new DetailedGroup({
//       timestamp: tms,
//       last_modified: tms,
//       affected_by: [],
//       slot: -1,
//       source: "",
//       destination: "",
//       originalObjectSIG: "",
//       applyFunction: null,
//       isDeleted: false,
//       SIGNATURE: "",
//       comment: "",
//       groupOwner: [],
//       id: "",
//       name: "",
//       nestedObjects: [],
//       parentGroup: "",
//       policies: [],
//       process: new Process({
//         currentPhase: new CurrentPhase({
//           errors: [],
//           data: [],
//           description: "",
//           index: BasicProcessPhase.NON_EXISTS,
//           name: "",
//         }),
//         phases: [],
//       }),
//       users: [],
//       isValid: false,
//       pending_objects: [],
//       execute: true,
//     });
//   }

//   static findGroupById(groups: DetailedGroup[], id: string): DetailedGroup {
//     return groups.find((g) => g.id === id) || DetailedGroup.empty();
//   }

//   toMap(): Record<string, any> {
//     const json = super.toMap();
//     json["object"] = {
//       ...json["object"],
//       name: this.name,
//     };
//     json["users"] = this.nestedUsers.map((u) => u.toMap());
//     json["policies"] = this.nestedPolicies.map((p) => p.toMap());
//     json["groupOwner"] = this.groupOwner;
//     json["nestedObjects"] = this.nestedObjects.map((o) => o.toMap());
//     return json;
//   }

//   toJson(): string {
//     return JSON.stringify(this.toMap());
//   }

//   copyWith({
//     users,
//     policies,
//     id,
//     groupOwner,
//     parentGroup,
//     name,
//     SIGNATURE,
//     comment,
//     process,
//     nestedObjects,
//     isValid,
//     isDeleted,
//     applyFunction,
//     originalObjectSIG,
//     source,
//     destination,
//     slot,
//     timestamp,
//     last_modified,
//     pending_objects,
//     affected_by,
//     execute,
//   }: {
//     users?: DetailedUser[];
//     policies?: DetailedPolicy[];
//     id?: string;
//     groupOwner?: string[];
//     parentGroup?: string;
//     name?: string;
//     SIGNATURE?: string;
//     comment?: string;
//     process?: Process;
//     nestedObjects?: DetailedSafeObject[];
//     isValid?: boolean;
//     isDeleted?: boolean;
//     applyFunction?: ApplyFunction | null;
//     originalObjectSIG?: string;
//     source?: string;
//     destination?: string;
//     slot?: number;
//     timestamp?: number;
//     last_modified?: number;
//     pending_objects?: DetailedSafeObject[];
//     affected_by?: DetailedSafeObject[];
//     execute?: boolean;
//   }): DetailedGroup {
//     return new DetailedGroup({
//       slot: slot ?? this.slot,
//       last_modified: last_modified ?? this.last_modified,
//       timestamp: timestamp ?? this.timestamp,
//       source: source ?? this.source,
//       destination: destination ?? this.destination,
//       originalObjectSIG: originalObjectSIG ?? this.originalObjectSIG,
//       applyFunction: applyFunction ?? this.applyFunction,
//       isDeleted: isDeleted ?? this.isDeleted,
//       SIGNATURE: SIGNATURE ?? this.SIGNATURE,
//       comment: comment ?? this.comment,
//       groupOwner: groupOwner ?? this.groupOwner,
//       id: id ?? this.id,
//       name: name ?? this.name,
//       nestedObjects: nestedObjects ?? this.nestedObjects,
//       parentGroup: parentGroup ?? this.parentGroup,
//       policies: policies ?? this.nestedPolicies,
//       process: process ?? this.process,
//       users: users ?? this.nestedUsers,
//       isValid: isValid ?? this.isValid,
//       pending_objects: pending_objects ?? this.pending_objects,
//       affected_by: affected_by ?? this.affected_by,
//       execute: execute ?? this.execute,
//     });
//   }

//   inheritedCopyWith({
//     id,
//     cls,
//     parentGroup,
//     SIGNATURE,
//     comment,
//     isValid,
//     process,
//     nestedObjects,
//     isDeleted,
//     applyFunction,
//     originalObjectSIG,
//     source,
//     destination,
//     slot,
//     timestamp,
//     last_modified,
//     pending_objects,
//     affected_by,
//     uiMetaData,
//     execute,
//   }: Partial<DetailedGroup>): DetailedGroup {
//     return new DetailedGroup({
//       slot: slot ?? this.slot,
//       timestamp: timestamp ?? this.timestamp,
//       last_modified: last_modified ?? this.last_modified,
//       groupOwner: this.groupOwner,
//       policies: this.nestedPolicies,
//       users: this.nestedUsers,
//       name: this.name,
//       isValid: isValid ?? this.isValid,
//       process: process ?? this.process,
//       nestedObjects: nestedObjects ?? this.nestedObjects,
//       isDeleted: isDeleted ?? this.isDeleted,
//       applyFunction: applyFunction ?? this.applyFunction,
//       originalObjectSIG: originalObjectSIG ?? this.originalObjectSIG,
//       source: source ?? this.source,
//       destination: destination ?? this.destination,
//       id: id ?? this.id,
//       parentGroup: parentGroup ?? this.parentGroup,
//       SIGNATURE: SIGNATURE ?? this.SIGNATURE,
//       comment: comment ?? this.comment,
//       pending_objects: pending_objects ?? this.pending_objects,
//       affected_by: affected_by ?? this.affected_by,
//       execute: execute ?? this.execute,
//     });
//   }
// }
